/**
 * Adapts the live React Flow canvas state (`projectStore`'s `nodes`/`edges`)
 * into the library-independent {@see StoryGraph} shape TraversalEngine,
 * PlayModePanel, and PresentationMode all expect (playMode/README.md's
 * design goal: the engine "can be used standalone" — it never imports
 * React Flow types itself).
 *
 * **Handle-id caveat:** an edge's `sourceHandle` (which Condition-node
 * branching keys off — 'true'/'false', see condition.tsx) is only reliable
 * for edges connected during THIS session. `projectStore.openGraph()`
 * hydrates edges from the server without `source_pin`/`target_pin` (the
 * backend columns exist but aren't read/written yet — a pre-existing gap in
 * the graph app's own persistence, not introduced here), so a Condition
 * node's edges can lose their true/false association across a save+reload.
 * This adapter passes through whatever `sourceHandle` is present; it doesn't
 * paper over the gap.
 */

import type { Edge, Node } from '@xyflow/react';
import type { StoryConnection, StoryGraph, StoryNode } from './TraversalEngine';

export function toStoryGraph(graphId: string, nodes: Node[], edges: Edge[]): StoryGraph {
    const storyNodes = new Map<string, StoryNode>();
    for (const n of nodes) {
        storyNodes.set(n.id, {
            id: n.id,
            type: n.type ?? 'note',
            data: (n.data ?? {}) as Record<string, unknown>,
            parentId: n.parentId,
        });
    }

    const connections: StoryConnection[] = edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? 'out',
        targetHandle: e.targetHandle ?? 'in',
        data: {
            condition: typeof e.data?.condition === 'string' ? e.data.condition : undefined,
            label: typeof e.label === 'string' ? e.label : undefined,
        },
    }));

    // Prefer an explicit Entry node (doc 04's nested-graph pattern); the
    // engine/components already fall back to the first node in the map when
    // entryNodeId is undefined.
    const entryNode = nodes.find((n) => n.type === 'entry');

    return { id: graphId, nodes: storyNodes, connections, entryNodeId: entryNode?.id };
}
